"use client";

import React, { useState } from "react";
import { useRouter } from 'next/navigation';
import { Button } from "@repo/ui";
import { 
  Sparkles,
  Heart,
  Moon,
  Laptop,
  DollarSign,
  Clock,
  Users,
  Star,
  ArrowRight,
  ChevronDown
} from 'lucide-react';

export default function StudentsPage() {
  const router = useRouter();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  
  const handleStartQuiz = () => {
    router.push('/student-vip/quiz?promo=STUDENTVIP');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50/30">
      {/* Hero Section */}
      <section className="px-4 pt-20 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 px-5 py-2.5 rounded-full text-sm font-semibold mb-6 border border-purple-200">
              <Sparkles className="w-4 h-4" />
              🇨🇦 STUDENTS: YOUR FIRST 2 SESSIONS ARE FREE
            </div>
            
            {/* Trusted by component */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <div className="flex -space-x-3">
                <img 
                  className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-md" 
                  src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&q=80" 
                  alt="Student" 
                />
                <img 
                  className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-md" 
                  src="https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop&q=80" 
                  alt="Student" 
                />
                <img 
                  className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-md" 
                  src="https://images.unsplash.com/photo-1598550874175-4d0ef436c909?w=100&h=100&fit=crop&q=80" 
                  alt="Student" 
                />
                <img 
                  className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-md" 
                  src="https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=100&h=100&fit=crop&q=80" 
                  alt="Student" 
                />
                <img 
                  className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-md" 
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&q=80" 
                  alt="Student" 
                />
              </div>
              <span className="text-gray-600 font-medium">Trusted by 38,000+ Canadians</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-gray-900">your mental health</span>
              <br />
              <span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">
                is not a luxury.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              for real though—2 free sessions to start. no catch. 
              just real therapy for whatever's keeping you up at 3am.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg"
                onClick={handleStartQuiz}
                className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white px-10 py-5 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                Start Your Therapy Journey →
              </Button>
            </div>
            
            <p className="text-sm text-gray-500 font-medium mt-4">
              ✨ 2 complimentary sessions · then $45 (less than your uber eats) · cancel whenever
            </p>
          </div>
        </div>
      </section>


      {/* What We're Here For */}
      <section className="px-4 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-purple-600 font-semibold mb-4 text-sm uppercase tracking-wider">We see you</p>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-gray-900">The reality no one talks about</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Behind every "I'm fine" is a story that deserves to be heard.
              <span className="block mt-2 font-semibold text-purple-600">Your first 2 therapy sessions are free. Let's talk.</span>
            </p>
          </div>
          
          <div className="space-y-16">
            {/* Tab-like navigation */}
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="md:w-1/3">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">What it looks like:</h3>
                <p className="text-gray-600 mb-6">The daily struggles everyone sees (or tries to hide)</p>
              </div>
              
              <div className="md:w-2/3 grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-5 border border-purple-100 hover:border-purple-300 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">😰</span>
                    <h4 className="font-semibold text-gray-900">Sunday Scaries hit different</h4>
                  </div>
                  <p className="text-gray-600 text-sm">That pit in your stomach before Monday. Panic calculating grades at 2am.</p>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border border-purple-100 hover:border-purple-300 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🔥</span>
                    <h4 className="font-semibold text-gray-900">Burnt out since September</h4>
                  </div>
                  <p className="text-gray-600 text-sm">5 hours sleep. Living on caffeine. Can't remember not being tired.</p>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border border-purple-100 hover:border-purple-300 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">💸</span>
                    <h4 className="font-semibold text-gray-900">Money stress is real</h4>
                  </div>
                  <p className="text-gray-600 text-sm">Textbook or groceries? Working but still broke. Too proud to ask for help.</p>
                </div>
                
                <div className="bg-white rounded-2xl p-5 border border-purple-100 hover:border-purple-300 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🌙</span>
                    <h4 className="font-semibold text-gray-900">3am anxiety spiral</h4>
                  </div>
                  <p className="text-gray-600 text-sm">Brain won't shut off. Overthinking everything. Tomorrow feels impossible.</p>
                </div>
              </div>
            </div>
            
            {/* Internal monologue section */}
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="md:w-1/3">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">What it sounds like:</h3>
                <p className="text-gray-600 mb-6">The voice in your head that won't stop criticizing</p>
              </div>
              
              <div className="md:w-2/3">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="text-purple-600 text-xl font-bold">"</span>
                    <div>
                      <p className="text-gray-800 font-medium">You're not smart enough for this.</p>
                      <p className="text-gray-600 text-sm mt-1">Everyone else gets it. You're the only one struggling.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <span className="text-purple-600 text-xl font-bold">"</span>
                    <div>
                      <p className="text-gray-800 font-medium">You're falling behind everyone else.</p>
                      <p className="text-gray-600 text-sm mt-1">They have internships. You're wasting your potential.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <span className="text-purple-600 text-xl font-bold">"</span>
                    <div>
                      <p className="text-gray-800 font-medium">Nobody actually wants you here.</p>
                      <p className="text-gray-600 text-sm mt-1">That's why you weren't invited. You're too much.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <span className="text-purple-600 text-xl font-bold">"</span>
                    <div>
                      <p className="text-gray-800 font-medium">You're disappointing everyone.</p>
                      <p className="text-gray-600 text-sm mt-1">Your parents sacrificed for nothing. You're letting them down.</p>
                    </div>
                  </div>
                </div>
                
                <p className="text-center mt-6 text-purple-700 font-semibold">
                  💜 These thoughts aren't facts. They're anxiety lying to you.
                </p>
              </div>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="relative mt-16">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl blur-xl opacity-60" />
            <div className="relative bg-white rounded-3xl border border-purple-200 p-8 md:p-12 shadow-xl">
              <div className="max-w-3xl mx-auto text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-6">
                  <span className="text-3xl">💜</span>
                </div>
                
                <h3 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
                  You're not "just being dramatic"
                </h3>
                
                <div className="space-y-2 mb-8">
                  <p className="text-lg text-gray-700">
                    Your feelings are <span className="font-semibold text-purple-600">valid</span>.
                  </p>
                  <p className="text-lg text-gray-700">
                    Your struggles are <span className="font-semibold text-purple-600">real</span>.
                  </p>
                  <p className="text-lg text-gray-700">
                    And you deserve support that <span className="font-semibold text-purple-600">actually helps</span>.
                  </p>
                </div>
                
                <div className="flex flex-col items-center gap-4">
                  <Button 
                    size="lg"
                    onClick={handleStartQuiz}
                    className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white px-10 py-5 text-lg font-bold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    Start Your Therapy Journey →
                  </Button>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>No waitlists</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Book this week</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>100% confidential</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Combined Solution Section */}
      <section id="how-it-works" className="px-4 py-20 bg-gradient-to-b from-white to-purple-50/30 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-5 py-2.5 rounded-full text-sm font-bold mb-6 border border-purple-200">
              <span>✨</span>
              <span>THE SOLUTION BUILT FOR YOU</span>
              <span>✨</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-gray-900">therapy that's actually</span>
              <br />
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">built for your life</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              no waitlists. no "have you tried yoga?" no judgment.
              <span className="block mt-2 font-semibold text-purple-600">just real help in 3 stupid-easy steps:</span>
            </p>
          </div>
          
          {/* 3-Step Process */}
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-purple-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center font-bold text-purple-700 text-xl">
                  1
                </div>
                <span className="text-xs bg-purple-100 text-purple-600 px-3 py-1 rounded-full font-bold">2 MIN QUIZ</span>
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">spill your feels (safely)</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                quick quiz about what's weighing on you, your budget, and the vibe you need from a therapist
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-purple-600 font-semibold">
                  🔒 stays between us, promise
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-pink-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-pink-200 rounded-full flex items-center justify-center font-bold text-pink-700 text-xl">
                  2
                </div>
                <span className="text-xs bg-pink-100 text-pink-600 px-3 py-1 rounded-full font-bold">4 MATCHES</span>
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">meet your people</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                get 4 therapists who actually get it - check their profiles, see when they're free, pick your fave
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-pink-600 font-semibold">
                  💜 curated just for you
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-green-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-200 to-green-300 rounded-full flex items-center justify-center font-bold text-green-800 text-xl">
                  3
                </div>
                <span className="text-xs bg-green-200 text-green-700 px-3 py-1 rounded-full font-bold animate-pulse">2 FREE SESSIONS</span>
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">get the support you deserve</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                book when you're ready - this week, next week, whenever. continue after at your chosen rate (from $45)
              </p>
              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-xs text-green-700 font-semibold">
                  ✨ no commitment required
                </p>
              </div>
            </div>
          </div>
          
          {/* The Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-xl">🫶</span>
                <span>what makes us different</span>
              </h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span className="text-gray-700">therapists who get academic pressure, dating drama & family expectations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span className="text-gray-700">87% of students keep going after free sessions (it actually helps)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span className="text-gray-700">online video sessions from your safe space</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span className="text-gray-700">switch therapists anytime if the vibe is off</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 border border-green-100">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-xl">💯</span>
                <span>the actual facts</span>
              </h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700"><span className="font-semibold">100% confidential</span> (parents will never know)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700"><span className="font-semibold">works with student insurance</span> & most benefits</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700"><span className="font-semibold">book this week</span> (availability varies by therapist)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700"><span className="font-semibold">cancel anytime</span> (no contracts or guilt trips)</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* CTA */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-200 to-pink-200 rounded-3xl blur-xl opacity-40" />
            <div className="relative bg-white rounded-3xl p-8 border border-purple-200 shadow-lg">
              <div className="max-w-2xl mx-auto text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-4">
                  <span className="text-2xl">💭</span>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  your brain at 3am: "what if everyone secretly hates me?"
                </h3>
                
                <p className="text-lg text-gray-700 mb-2">
                  your brain right now: <span className="font-semibold text-purple-600">"but what if therapy is weird?"</span>
                </p>
                
                <p className="text-gray-600 mb-6">
                  here's the thing - the overthinking won't stop on its own. trust us (and 38k+ canadians who took the leap)
                </p>
                
                <Button 
                  size="lg"
                  onClick={handleStartQuiz}
                  className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white px-10 py-5 text-lg font-bold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  take the quiz already →
                </Button>
                
                <div className="flex items-center justify-center gap-4 mt-5 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <span className="text-green-500">✓</span> 2 minutes max
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-green-500">✓</span> 2 free sessions
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-green-500">✓</span> no catch
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Therapist Options with Community Model */}
      <section id="pricing" className="px-4 py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-5 py-2.5 rounded-full text-sm font-bold mb-6 border border-purple-200">
              <span>🫶</span>
              <span>YOU'RE JOINING A COMMUNITY</span>
              <span>🫶</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-gray-900">welcome to therapy that</span>
              <br />
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">actually fits your budget</span>
            </h2>
          </div>

          {/* How Our Community Works */}
          <div className="relative mb-12">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-200 to-pink-200 rounded-3xl blur-2xl opacity-30" />
            <div className="relative bg-white rounded-3xl p-8 md:p-12 border-2 border-purple-200 shadow-xl">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-10">
                  <h3 className="text-3xl md:text-4xl font-bold mb-6">
                    <span className="text-gray-900">we're </span>
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">38,000 strong</span>
                    <span className="text-gray-900"> and growing</span>
                  </h3>
                  <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
                    when you join, you're not just getting therapy - you're joining a community where 
                    <span className="font-semibold text-purple-600"> we all look out for each other</span>
                  </p>

                  {/* Community Visual */}
                  <div className="bg-gradient-to-br from-purple-50 via-white to-pink-50 rounded-2xl p-8 mb-8 max-w-4xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="text-left space-y-4">
                        <h4 className="font-bold text-gray-900 text-lg">how we work together:</h4>
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-purple-600">1</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">we each pay what we can</span> - anywhere from $45 to $105, based on our situation
                            </p>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-purple-600">2</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">together we balance out</span> - those who can pay more help those who can't
                            </p>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-purple-600">3</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">we all get the help we need</span> - no one turned away for financial reasons
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl p-6 shadow-sm border border-purple-100">
                        <p className="text-sm font-bold text-purple-700 mb-4">YOUR IMPACT IN OUR COMMUNITY</p>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">paying $45-60?</span>
                            <span className="text-xs font-semibold text-purple-600">we support you</span>
                          </div>
                          <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">paying $65-85?</span>
                            <span className="text-xs font-semibold text-purple-600">we balance together</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">paying $90-105?</span>
                            <span className="text-xs font-semibold text-purple-600">you support others</span>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-xs text-gray-500 text-center">
                            everyone contributes • everyone benefits
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Therapist Info */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-center mb-8 text-gray-900">your dedicated intern therapist:</h3>
            
            <div className="max-w-3xl mx-auto">
              {/* Intern Therapists */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Intern Therapists</h3>
                
                <div className="mb-4">
                  <div className="mb-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">STANDARD RATE</p>
                    <span className="text-3xl font-bold text-gray-900">$105</span>
                    <span className="text-sm text-gray-600 ml-1">per session</span>
                  </div>
                  <div className="bg-green-50 text-green-700 px-3 py-1.5 rounded-lg inline-block">
                    <span className="text-sm font-bold">Pay what you can: $45-105</span>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Talk to someone who gets you. Master's-level therapists with fresh training and genuine passion for helping you feel better.
                </p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5">✓</span>
                    <span className="text-sm text-gray-700">Double the expertise: Your therapist + their licensed supervisor working on your care</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5">✓</span>
                    <span className="text-sm text-gray-700">Currently training in the latest evidence-based approaches that actually work</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5">✓</span>
                    <span className="text-sm text-gray-700">Lower caseloads mean more energy and attention for you (10-15 vs 30+ clients)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5">✓</span>
                    <span className="text-sm text-gray-700">Understand modern challenges - from social media stress to remote work burnout</span>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                  <span className="font-semibold">Scope limitations:</span> Cannot work with severe mental illness, active addiction, recent suicide attempts, eating disorders, trauma therapy, or court-mandated therapy
                </div>
              </div>
            </div>
            
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button 
              size="lg"
              onClick={() => router.push('/student-vip/quiz?promo=STUDENTVIP')}
              className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white px-10 py-5 text-lg font-bold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              find your match →
            </Button>
          </div>
        </div>
      </section>

      {/* Real Reviews */}
      <section className="px-4 py-16 bg-white rounded-3xl mx-4 my-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-gray-900">
            What People Are Saying
          </h2>
          <p className="text-center text-lg text-gray-600 mb-12">
            <span className="inline-flex items-center gap-2">
              <span className="text-yellow-500">★</span>
              {/* <span className="font-semibold">4.6/5 on Trustpilot</span> */}
              {/* <span className="text-gray-400">•</span> */}
              <span>Verified Reviews</span>
            </span>
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-3xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed italic">
                "After leaving university I felt lost. I was unable to afford therapy due to the high cost of most therapists. Alli made it possible for me to get the help I needed."
              </p>
              <p className="font-semibold text-gray-900">— Amberley</p>
              {/* <p className="text-sm text-gray-600">Verified on Trustpilot</p> */}
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-3xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed italic">
                "I opted for the most affordable tier, seeing a student who is doing their practicum hours through Alli. The quality has been amazing and exactly what I needed."
              </p>
              <p className="font-semibold text-gray-900">— Shan W.</p>
              {/* <p className="text-sm text-gray-600">Verified on Trustpilot</p> */}
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-3xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed italic">
                "I have worked with 2 fourth year students so far and my experience was great! They really understood what I was going through."
              </p>
              <p className="font-semibold text-gray-900">— Dee</p>
              {/* <p className="text-sm text-gray-600">Verified on Trustpilot</p> */}
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-3xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed italic">
                "Alli therapy has helped me get through one of the hardest times in my life. My therapist has been extremely understanding and helpful."
              </p>
              <p className="font-semibold text-gray-900">— Fizza N.</p>
              {/* <p className="text-sm text-gray-600">Verified on Trustpilot</p> */}
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-3xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed italic">
                "I was able to evaluate therapists first and found a therapist that I connected with early on. The matching process really works!"
              </p>
              <p className="font-semibold text-gray-900">— Chris H.</p>
              {/* <p className="text-sm text-gray-600">Verified on Trustpilot</p> */}
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-3xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed italic">
                "Everyone is really professional, they get back to you promptly. The system used for booking appointments is easy and stress-free."
              </p>
              <p className="font-semibold text-gray-900">— Kitty</p>
              {/* <p className="text-sm text-gray-600">Verified on Trustpilot</p> */}
            </div>
          </div>

          {/* <div className="text-center mt-8">
            <a
              href="https://www.trustpilot.com/review/alli.io"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold"
            >
              Read more stories on Trustpilot
              <ArrowRight className="w-4 h-4" />
            </a>
          </div> */}
        </div>
      </section>


      {/* FAQ */}
      <section className="px-4 py-16 bg-white rounded-3xl mx-4 my-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 text-gray-900">
            Questions We Get All The Time
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: "How do I get my 2 free sessions?",
                a: "Sign up with your student email (.ca or .edu), take the 2-minute matching quiz, and book your first session. Your first 2 sessions are automatically free - no promo code needed."
              },
              {
                q: "What happens after my 2 free sessions?",
                a: "You can continue therapy at your chosen rate ($45-105 with your intern therapist). No pressure to continue - it's totally up to you."
              },
              {
                q: "How does the pay-what-you-can model work?",
                a: "You choose your rate based on what you can afford. No proof required, no questions asked. You can change your rate anytime through your account - takes 30 seconds."
              },
              {
                q: "What kind of therapist will I get?",
                a: "You'll be matched with an intern therapist who's completing their master's practicum hours. They work under supervision by licensed therapists, so you get 2-for-1 expertise at a student-friendly price."
              },
              {
                q: "Is this really confidential? Will my parents find out?",
                a: "100% confidential. We don't share information with your university, parents, or anyone else. The only exceptions are if you're at immediate risk of harm (standard for all therapy)."
              },
              {
                q: "Can I switch therapists if it's not working out?",
                a: "Yes! Sometimes the vibe just isn't right. You can switch therapists anytime - no awkward conversations required. Just let us know and we'll help you find a better match."
              },
              {
                q: "Do I need to be on camera?",
                a: "Video sessions are recommended for the best therapeutic experience, but you can discuss preferences with your therapist. They're flexible and understanding."
              },
              {
                q: "How quickly can I start?",
                a: "Most students can book their first session within the same week. After taking the quiz, you'll see your matched therapists' availability and can book right away."
              },
              {
                q: "Does this work with my student insurance?",
                a: "Yes! We provide official receipts that work with most student insurance plans. You can submit these for reimbursement if your plan covers psychotherapy."
              },
              {
                q: "What if I'm struggling with something serious?",
                a: "Intern therapists can help with most issues like anxiety, depression, and relationship problems. For severe mental illness, active addiction, or eating disorders, we'll refer you to appropriate resources. In crisis? Call 911 or text CONNECT to 741741."
              },
              {
                q: "Are international students eligible?",
                a: "Yes! If you're enrolled at a Canadian university, you're eligible regardless of where you're from. We support all students in Canada."
              },
              {
                q: "Can I book sessions around my class schedule?",
                a: "Absolutely. Most therapists offer flexible hours including evenings and weekends. You can see availability before booking and schedule around your classes."
              }
            ].map((faq, index) => (
              <div key={index} className="border border-purple-200/50 rounded-2xl overflow-hidden bg-gradient-to-r from-purple-50/50 to-purple-100/30 shadow-sm hover:shadow-md transition-all">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-100/50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-purple-600 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-6xl font-bold mb-4 text-gray-900">
            Your Mental Health Matters
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Two free sessions to start. No catch. Just real support when you need it most.
          </p>
          <Button 
            size="lg"
            onClick={() => router.push('/student-vip/quiz?promo=STUDENTVIP')}
            className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white px-10 py-5 text-lg font-bold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            Start Your Therapy Journey →
          </Button>
          <p className="text-sm text-gray-500 mt-4 font-medium">
            Join 38,000+ Canadians who've taken the first step
          </p>
        </div>
      </section>
    </div>
  );
}